// Adapted from https://github.com/bigcommerce/big-design/blob/master/packages/big-design/src/components/Datepicker/Header.tsx
import { forwardRef, memo } from 'react';
import ReactDatePicker from 'react-datepicker';
import styled from 'styled-components';
import { Button, Text, Flex, Input } from '@bigcommerce/big-design';
import {
	ChevronLeftIcon,
	ChevronRightIcon,
} from '@bigcommerce/big-design-icons';
import { theme as defaultTheme } from '@bigcommerce/big-design-theme';

const StyledDatepicker = styled.div`
	& .react-datepicker-wrapper {
		display: inline-block;
		width: 100%;
	}
	& .react-datepicker__input-container {
		display: inline-block;
		font-family: ${({ theme }) => theme.typography.fontFamily};
		position: relative;
		width: 100%;
	}
	& .react-datepicker__month-container {
		margin: ${({ theme }) => theme.spacing.medium};
	}
	& .react-datepicker__month {
		margin-top: ${({ theme }) => theme.spacing.xSmall};
		text-align: center;
	}
	& .react-datepicker__header {
		border-top-left-radius: ${({ theme }) => theme.spacing.xxSmall};
		border-top-right-radius: ${({ theme }) => theme.spacing.xxSmall};
		position: relative;
		text-align: center;
	}
	& .react-datepicker__current-month {
		font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
	}
	& .react-datepicker__day-names,
	.react-datepicker__week {
		white-space: nowrap;
	}
	& .react-datepicker__day,
	.react-datepicker__month-text {
		cursor: pointer;
	}
	& .react-datepicker__day-name,
	.react-datepicker__day {
		color: ${({ theme }) => theme.colors.secondary60};
		display: inline-block;
		line-height: ${({ theme }) => theme.lineHeight.xLarge};
		margin: ${({ theme }) => theme.spacing.xxSmall};
		text-align: center;
		width: ${({ theme }) => theme.lineHeight.xLarge};
	}
	& .react-datepicker__day:hover {
		background: ${({ theme }) => theme.colors.primary10};
		border-radius: ${({ theme }) => theme.borderRadius.normal};
		color: ${({ theme }) => theme.colors.secondary70};
		outline: none;
	}
	& .react-datepicker__day:focus {
		border-radius: ${({ theme }) => theme.borderRadius.normal};
		color: ${({ theme }) => theme.colors.secondary70};
		outline: none;
	}
	& .react-datepicker__day--keyboard-selected {
		background: ${({ theme }) => theme.colors.primary10};
		border-radius: ${({ theme }) => theme.borderRadius.normal};
		color: ${({ theme }) => theme.colors.secondary70};
		outline: none;
	}
	& .react-datepicker__day:active,
	& .react-datepicker__day--in-selecting-range {
		background: ${({ theme }) => theme.colors.primary20};
		border-radius: ${({ theme }) => theme.borderRadius.normal};
		color: ${({ theme }) => theme.colors.secondary70};
		outline: none;
	}
	& .react-datepicker__day--today::after {
		background: ${({ theme }) => theme.colors.primary};
		bottom: ${({ theme }) => theme.helpers.remCalc(1)};
		content: '';
		display: block;
		height: ${({ theme }) => theme.helpers.remCalc(2)};
		margin-left: ${({ theme }) => theme.spacing.xxSmall};
		position: relative;
		width: ${({ theme }) => theme.spacing.xLarge};
	}
	& .react-datepicker__day--selected,
	& .react-datepicker__day--in-range {
		background: ${({ theme }) => theme.colors.primary};
		border-radius: ${({ theme }) => theme.borderRadius.normal};
		color: ${({ theme }) => theme.colors.white};
	}
	&
		.react-datepicker__day--selected:hover.react-datepicker__day--today::after {
		bottom: ${({ theme }) => theme.helpers.remCalc(1)};
	}
	& .react-datepicker__day--selected.react-datepicker__day--today::after {
		bottom: -${({ theme }) => theme.spacing.xxSmall};
	}
	& .react-datepicker__day--disabled {
		color: ${({ theme }) => theme.colors.secondary};
	}
	& .react-datepicker__day--outside-month {
		opacity: 0;
		visibility: hidden;
	}
	& .react-datepicker__close-icon {
		cursor: pointer;
		background-color: transparent;
		border: 0;
		outline: 0;
		padding: 0 6px 0 0;
		position: absolute;
		top: 0;
		right: 0;
		height: 100%;
		display: table-cell;
		vertical-align: middle;
	}
	& .react-datepicker__close-icon::after {
		cursor: pointer;
		background-color: ${({ theme }) => theme.colors.primary};
		color: #fff;
		border-radius: 50%;
		height: 16px;
		width: 16px;
		padding: 2px;
		font-size: 12px;
		line-height: 1;
		text-align: center;
		display: table-cell;
		vertical-align: middle;
		content: '×';
	}
	& .bc-datepicker {
		${({ theme }) => theme.shadow.floating};
		background: ${({ theme }) => theme.colors.white};
		display: flex;
		font-family: ${({ theme }) => theme.typography.fontFamily};
		font-size: ${({ theme }) => theme.typography.fontSize.small};
		position: relative;
	}
	& .react-datepicker-popper {
		z-index: ${({ theme }) => theme.zIndex.popover};
	}
`;

StyledDatepicker.defaultProps = { theme: defaultTheme };

const monthFormatter = Intl.DateTimeFormat('en-US', {
	month: 'long',
});

const monthsLong = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((month) =>
	monthFormatter.format(new Date(0, month, 1)),
);

function Header(props) {
	const {
		date = new Date(),
		decreaseMonth,
		increaseMonth,
		prevMonthButtonDisabled,
		nextMonthButtonDisabled,
	} = props;

	return (
		<Flex alignItems="center" justifyContent="space-between">
			<Button
				title="View previous month."
				type="button"
				iconOnly={<ChevronLeftIcon title="View previous month." />}
				onClick={decreaseMonth}
				disabled={prevMonthButtonDisabled}
				variant="subtle"
			/>

			<Text as="span" marginBottom="none" bold>{`${
				monthsLong[date.getMonth()]
			} ${date.getFullYear()}`}</Text>

			<Button
				title="View next month."
				type="button"
				iconOnly={<ChevronRightIcon title="View next month." />}
				onClick={increaseMonth}
				disabled={nextMonthButtonDisabled}
				variant="subtle"
			/>
		</Flex>
	);
}

const RawDateRangePicker = (props) => {
	const {
		customInput,
		dateFormat,
		error,
		forwardedRef,
		label,
		...datePickerProps
	} = props;

	return (
		<StyledDatepicker>
			<ReactDatePicker
				renderCustomHeader={({
					date,
					decreaseMonth,
					increaseMonth,
					prevMonthButtonDisabled,
					nextMonthButtonDisabled,
				}) => (
					<Header
						date={date}
						decreaseMonth={decreaseMonth}
						increaseMonth={increaseMonth}
						prevMonthButtonDisabled={prevMonthButtonDisabled}
						nextMonthButtonDisabled={nextMonthButtonDisabled}
					/>
				)}
				customInput={
					customInput || <Input label={label} error={error} />
				}
				className="calendar-input"
				calendarClassName="bc-datepicker"
				dateFormat={dateFormat || 'EE, dd MMM, yyyy'}
				ref={forwardedRef}
				{...datePickerProps}
			/>
		</StyledDatepicker>
	);
};

const DateRangePicker = memo(
	forwardRef((props, ref) => (
		<RawDateRangePicker {...props} forwardedRef={ref} />
	)),
);
DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker };
